import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileCheck2, FileText, ScrollText, Wrench } from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import { buildOrderReportPayload, OrdersPage, orderDocumentLabels, orderMachineFromRecord, renderOrderDocumentHtml, type OrderDocumentMachine } from '@forq/orders';
import type { NewCustomerDraft, OrderCustomerOption, OrderDocumentKind, OrderDocumentRecord, OrderGateway, OrderRecord } from '@forq/orders-data';
import { fetchReportPdf, openPdfBlob, reportServerConfigured } from '../reportServer';
import {
  attachmentDocumentCategoryLabels,
  emptyCompanyProfile,
  isAttachmentDocumentSuggested,
  type AttachmentDocumentGateway,
  type AttachmentDocumentRecord,
  type CompanyProfileGateway,
  type TenantCompanyProfile,
} from '@forq/platform-data';
import type { CustomersGateway } from '@forq/customers-data';
import type { PartsGateway } from '@forq/parts-data';
import type { FinanceGateway } from '@forq/finance-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { MastersGateway } from '@forq/masters-data';
import { OrderBillingPanel } from './OrderBillingPanel';
import { OrderPartsPanel } from './OrderPartsPanel';

interface OrdersProfessionalWorkspaceProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: OrderGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly attachmentDocumentGateway: AttachmentDocumentGateway;
  readonly financeGateway: FinanceGateway;
  readonly machinesGateway: MachinesGateway;
  readonly masterdataGateway: MasterdataGateway;
  readonly mastersGateway: MastersGateway;
  readonly customersGateway: CustomersGateway;
  readonly partsGateway: PartsGateway;
  /** Läuft der Bestelldienst des FORQ-Servers? Ohne ihn bleiben Suche und Absenden gesperrt. */
  readonly partsOrderingAvailable: boolean;
}

/** Vorgangstext für den Regel-Abgleich der Zusatzdokumente: Titel + Positionen (inkl. Positionstext). */
function orderTextOf(order: OrderRecord): string {
  const positions = order.positions
    .map((position) => `${position.description} ${(position.customerText ?? '').replace(/<[^>]*>/g, ' ')} ${position.internalText ?? ''}`)
    .join(' ');
  return `${order.title} ${positions}`;
}

interface DocumentStation {
  readonly kind: OrderDocumentKind;
  readonly icon: typeof FileText;
  readonly hint: string;
}
/** ARISTA-Belegstrecke: die Belegarten in ihrer natürlichen Reihenfolge (Angebot → Auftragsbestätigung
 *  → Werkstattauftrag → Lieferschein → Rechnung). Werkstattauftrag nur für werkstattrelevante Profile. */
const documentStations: readonly DocumentStation[] = [
  { kind: 'offer', icon: FileText, hint: 'Freibleibendes Angebot mit Positionen und Summen' },
  { kind: 'confirmation', icon: FileCheck2, hint: 'Kaufmännische Bestätigung mit Positionen und Summen' },
  { kind: 'workshop-order', icon: Wrench, hint: 'Interne Arbeitsanweisung für die Werkstatt' },
  { kind: 'delivery-note', icon: ScrollText, hint: 'Positionen ohne Preise, mit Empfangsbestätigung' },
];
const workshopProfiles = ['service', 'internal', 'general'];

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Beleg konnte nicht erstellt werden.';
}

export function OrdersProfessionalWorkspace(props: OrdersProfessionalWorkspaceProps) {
  const { tenant, gateway, companyGateway, attachmentDocumentGateway, permissions } = props;
  const { financeGateway, machinesGateway, masterdataGateway, mastersGateway, customersGateway } = props;
  const { partsGateway, partsOrderingAvailable } = props;

  // Inline im Vorgang einen neuen Kunden anlegen (Vorgang-Zentrierung): erzeugt den Kunden über den
  // customersGateway und liefert die fertige OrderCustomerOption zum sofortigen Übernehmen zurück.
  const handleCreateCustomer = useCallback(async (draft: NewCustomerDraft): Promise<OrderCustomerOption> => {
    const created = await customersGateway.createCustomer({
      tenantId: tenant.id,
      kind: draft.kind,
      companyName: draft.kind === 'company' ? draft.companyName : undefined,
      firstName: draft.kind === 'person' ? draft.firstName : undefined,
      lastName: draft.kind === 'person' ? draft.lastName : undefined,
      email: draft.email || undefined,
      phone: draft.phone || undefined,
      billingAddress: (draft.street || draft.postalCode || draft.city)
        ? { street: draft.street, postalCode: draft.postalCode, city: draft.city, countryCode: 'DE' }
        : undefined,
    });
    const displayName = created.displayName || (draft.kind === 'company' ? draft.companyName : `${draft.firstName} ${draft.lastName}`.trim());
    return {
      id: created.id,
      number: created.number,
      displayName,
      contactName: '',
      email: draft.email,
      phone: draft.phone,
      billingAddress: { kind: 'billing', label: 'Rechnungsadresse', companyName: displayName, street: draft.street, postalCode: draft.postalCode, city: draft.city, countryCode: 'DE' },
      deliveryAddress: null,
    };
  }, [customersGateway, tenant.id]);
  // The opened order is driven by the record URL (/vorgaenge/:orderNumber) inside OrdersPage,
  // which reports it here — no DOM scraping, and it survives reload / deep links.
  const [selectedOrder, setSelectedOrder] = useState<OrderRecord | null>(null);
  // Erhöht sich nach dem Abrechnen -> OrdersPage lädt die Liste neu -> der Status im Fenster ist sofort aktuell.
  const [reloadKey, setReloadKey] = useState(0);
  const [documents, setDocuments] = useState<readonly OrderDocumentRecord[]>([]);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [busyKind, setBusyKind] = useState<OrderDocumentKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<OrderDocumentRecord | null>(null);
  const [attachmentDocs, setAttachmentDocs] = useState<readonly AttachmentDocumentRecord[]>([]);
  const [attachmentPrompt, setAttachmentPrompt] = useState<{ readonly doc: OrderDocumentRecord; readonly selected: ReadonlySet<string> } | null>(null);

  const canCreate = permissions.has('orders.edit');

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [companyGateway, tenant.id]);

  // Zusatzdokumente/Prüflisten des Mandanten -- werden beim Werkstattauftrag zum Mitdrucken angeboten.
  useEffect(() => {
    let active = true;
    attachmentDocumentGateway.listDocuments(tenant.id).then((list) => { if (active) setAttachmentDocs(list); }).catch(() => {});
    return () => { active = false; };
  }, [attachmentDocumentGateway, tenant.id]);

  useEffect(() => {
    if (!selectedOrder) { setDocuments([]); return undefined; }
    let active = true;
    setError(null);
    gateway.listDocuments(tenant.id, selectedOrder.id)
      .then((list) => { if (active) setDocuments(list); })
      .catch(() => { if (active) setDocuments([]); });
    return () => { active = false; };
  }, [gateway, tenant.id, selectedOrder]);

  // Belegstrecke dieses Vorgangs: Werkstattauftrag nur für werkstattrelevante Profile.
  const stations = useMemo(() => {
    const showWorkshop = selectedOrder ? workshopProfiles.includes(selectedOrder.profile) : false;
    return documentStations.filter((station) => station.kind !== 'workshop-order' || showWorkshop);
  }, [selectedOrder]);

  // Jüngster Beleg je Belegart = der Meilenstein der Station (Nummer + Ausstellungsdatum).
  const latestByKind = useMemo(() => {
    const map: Partial<Record<OrderDocumentKind, OrderDocumentRecord>> = {};
    for (const document of documents) {
      const current = map[document.kind];
      if (!current || document.createdAt > current.createdAt) map[document.kind] = document;
    }
    return map;
  }, [documents]);

  const createDocument = useCallback(async (kind: OrderDocumentKind) => {
    if (!selectedOrder) return;
    setBusyKind(kind);
    setError(null);
    try {
      const created = await gateway.createDocument({ tenantId: tenant.id, orderId: selectedOrder.id, kind });
      setDocuments((current) => [created, ...current]);
      // Beim Werkstattauftrag: fragen, welche hinterlegten Zusatzdokumente mitgedruckt werden sollen
      // (passende sind vorangehakt). Sonst wie bisher direkt die Beleg-Vorschau öffnen.
      if (kind === 'workshop-order' && attachmentDocs.length > 0) {
        const orderText = orderTextOf(selectedOrder);
        const selected = new Set(attachmentDocs.filter((document) => isAttachmentDocumentSuggested(document, orderText)).map((document) => document.id));
        setAttachmentPrompt({ doc: created, selected });
      } else {
        setPreview(created);
      }
    } catch (createError) {
      setError(messageOf(createError));
    } finally {
      setBusyKind(null);
    }
  }, [gateway, tenant.id, selectedOrder, attachmentDocs]);

  const openAttachment = useCallback(async (documentId: string) => {
    try {
      const full = await attachmentDocumentGateway.getDocument(tenant.id, documentId);
      const win = window.open('', '_blank');
      if (win) {
        win.document.title = full.title;
        win.document.body.style.margin = '0';
        win.document.body.innerHTML = `<iframe src="${full.content}" title="${full.title}" style="border:0;width:100vw;height:100vh"></iframe>`;
      }
    } catch (openError) {
      setError(messageOf(openError));
    }
  }, [attachmentDocumentGateway, tenant.id]);

  const finishAttachmentPrompt = useCallback((withSelected: boolean) => {
    if (!attachmentPrompt) return;
    if (withSelected) attachmentPrompt.selected.forEach((documentId) => void openAttachment(documentId));
    setPreview(attachmentPrompt.doc);
    setAttachmentPrompt(null);
  }, [attachmentPrompt, openAttachment]);

  const toggleAttachment = useCallback((documentId: string) => {
    setAttachmentPrompt((current) => {
      if (!current) return current;
      const selected = new Set(current.selected);
      if (selected.has(documentId)) selected.delete(documentId); else selected.add(documentId);
      return { ...current, selected };
    });
  }, []);

  return (
    <div className="orders-professional-workspace">
      <OrdersPage
        {...props}
        onSelectedOrderChange={setSelectedOrder}
        onCreateCustomer={handleCreateCustomer}
        reloadKey={reloadKey}
        detailChildren={selectedOrder ? (
          <>
          <section className="orders-document-center" aria-label="Beleg-Aktions-Center">
            <header>
              <div>
                <span>Beleg-Aktions-Center</span>
                <h2>{selectedOrder.number} · {selectedOrder.title}</h2>
                <p>Belege der Reihe nach — jeder Beleg bekommt eine eigene Nummer und hält den Vorgang zum Ausstellungszeitpunkt fest.</p>
              </div>
            </header>

            {error ? <div className="orders-document-center__message is-error">{error}</div> : null}
            {!canCreate ? <p className="orders-document-center__hint">Zum Erstellen von Belegen fehlt die Berechtigung.</p> : null}

            <ol className="orders-belegstrecke">
              {stations.map(({ kind, icon: Icon, hint }, index) => {
                const issued = latestByKind[kind];
                const count = documents.filter((document) => document.kind === kind).length;
                return (
                  <li className={`orders-belegstrecke__station${issued ? ' is-done' : ''}`} key={kind}>
                    <span className="orders-belegstrecke__num">{index + 1}</span>
                    <div className="orders-belegstrecke__main">
                      <strong><Icon size={13} /> {orderDocumentLabels[kind]}</strong>
                      <span className="orders-belegstrecke__milestone">
                        {issued
                          ? `Ausgestellt am ${shortDate(issued.createdAt)} · ${issued.number}${count > 1 ? ` · ${count} Belege` : ''}`
                          : hint}
                      </span>
                    </div>
                    <div className="orders-belegstrecke__actions">
                      {issued ? (
                        <button type="button" className="is-ghost" onClick={() => setPreview(issued)}><FileText size={13} /> Öffnen</button>
                      ) : null}
                      {canCreate ? (
                        <button type="button" disabled={busyKind !== null} onClick={() => void createDocument(kind)}>
                          {busyKind === kind ? 'Wird erstellt …' : issued ? 'Neu erstellen' : 'Erstellen'}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>

            {documents.length > 0 ? (
              <details className="orders-belegstrecke__history">
                <summary>Alle Belege ({documents.length})</summary>
                <div className="orders-document-history">
                  <div className="orders-document-history__head">
                    <span>Beleg</span><span>Nummer</span><span>Erstellt</span><span>Von</span><span>Aktion</span>
                  </div>
                  {documents.map((document) => (
                    <div className="orders-document-history__row" key={document.id}>
                      <span><FileText size={14} /><strong>{orderDocumentLabels[document.kind]}</strong></span>
                      <span>{document.number}</span>
                      <span>{new Date(document.createdAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                      <span>{document.createdBy || '—'}</span>
                      <button type="button" onClick={() => setPreview(document)}><FileText size={14} /> Öffnen</button>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </section>

          {/* Ersatzteile vor der Abrechnung: erst kommt das Teil, dann die Rechnung. */}
          <OrderPartsPanel
            tenant={tenant}
            order={selectedOrder}
            permissions={permissions}
            gateway={partsGateway}
            orderingAvailable={partsOrderingAvailable}
            stepNumber={stations.length + 1}
          />

          <OrderBillingPanel
            tenant={tenant}
            order={selectedOrder}
            permissions={permissions}
            financeGateway={financeGateway}
            ordersGateway={gateway}
            machinesGateway={machinesGateway}
            masterdataGateway={masterdataGateway}
            mastersGateway={mastersGateway}
            stepNumber={stations.length + 2}
            onBilled={() => setReloadKey((key) => key + 1)}
          />
          </>
        ) : null}
      />

      {attachmentPrompt ? (
        <AttachmentPrintPrompt
          workshopNumber={attachmentPrompt.doc.number}
          documents={attachmentDocs}
          selected={attachmentPrompt.selected}
          onToggle={toggleAttachment}
          onWorkshopOnly={() => finishAttachmentPrompt(false)}
          onPrintSelected={() => finishAttachmentPrompt(true)}
        />
      ) : null}

      {preview ? <OrderDocumentModal document={preview} company={company} machinesGateway={machinesGateway} tenantId={tenant.id} onClose={() => setPreview(null)} /> : null}
    </div>
  );
}

function AttachmentPrintPrompt({ workshopNumber, documents, selected, onToggle, onWorkshopOnly, onPrintSelected }: {
  readonly workshopNumber: string;
  readonly documents: readonly AttachmentDocumentRecord[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (documentId: string) => void;
  readonly onWorkshopOnly: () => void;
  readonly onPrintSelected: () => void;
}) {
  const selectedCount = documents.filter((document) => selected.has(document.id)).length;
  return (
    <div className="orders-attach-prompt" role="dialog" aria-modal="true" aria-label="Zusatzdokumente mitdrucken" onMouseDown={(event) => { if (event.target === event.currentTarget) onWorkshopOnly(); }}>
      <div className="orders-attach-prompt__panel">
        <header>
          <div>
            <span>Werkstattauftrag {workshopNumber} erstellt</span>
            <h3>Zusatzdokumente mitdrucken?</h3>
          </div>
        </header>
        <p className="orders-attach-prompt__hint">Passende Dokumente sind vorausgewählt. Ausgewählte werden zum Drucken in eigenen Tabs geöffnet.</p>
        <ul className="orders-attach-prompt__list">
          {documents.map((document) => (
            <li key={document.id}>
              <label>
                <input type="checkbox" checked={selected.has(document.id)} onChange={() => onToggle(document.id)} />
                <span className="orders-attach-prompt__title">{document.title}</span>
                <span className="orders-attach-prompt__tag">{attachmentDocumentCategoryLabels[document.category]}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="orders-attach-prompt__actions">
          <button type="button" className="is-secondary" onClick={onWorkshopOnly}>Nur Werkstattauftrag</button>
          <button type="button" onClick={onPrintSelected}>Werkstattauftrag + {selectedCount} Dokument{selectedCount === 1 ? '' : 'e'} öffnen</button>
        </div>
      </div>
    </div>
  );
}

/** Belegart -> Dateiname der JasperReports-Vorlage auf dem Server (.../render/<name>). */
const reportTemplateForKind: Record<OrderDocumentKind, string> = {
  offer: 'angebot',
  confirmation: 'auftragsbestaetigung',
  'delivery-note': 'lieferschein',
  'workshop-order': 'werkstattauftrag',
};

function OrderDocumentModal({ document: doc, company, machinesGateway, tenantId, onClose }: {
  readonly document: OrderDocumentRecord;
  readonly company: TenantCompanyProfile;
  readonly machinesGateway: MachinesGateway;
  readonly tenantId: string;
  readonly onClose: () => void;
}) {
  // Maschinen-Technikdaten aus dem Stamm nachladen -- ALLE Maschinen des Belegs: die des Vorgangs und
  // jede, die an einer Position hängt. Ein Angebot über mehrere Stapler bekommt so je Position einen
  // eigenen Technikblock samt Foto. Fehlgeschlagene Einzelabrufe werden still übersprungen.
  const [machine, setMachine] = useState<OrderDocumentMachine | undefined>(undefined);
  const [machines, setMachines] = useState<Record<string, OrderDocumentMachine>>({});
  useEffect(() => {
    const orderMachineId = doc.snapshot.machineId;
    const ids = Array.from(new Set([
      ...(orderMachineId ? [orderMachineId] : []),
      ...doc.snapshot.positions.map((position) => position.machineId).filter((id): id is string => !!id),
    ]));
    if (ids.length === 0) { setMachine(undefined); setMachines({}); return; }
    let active = true;
    void Promise.all(ids.map((id) => machinesGateway.getMachine(tenantId, id)
      .then((record) => [id, orderMachineFromRecord(record)] as const)
      .catch(() => null)))
      .then((entries) => {
        if (!active) return;
        const loaded: Record<string, OrderDocumentMachine> = {};
        for (const entry of entries) { if (entry) loaded[entry[0]] = entry[1]; }
        setMachines(loaded);
        setMachine(orderMachineId ? loaded[orderMachineId] : undefined);
      });
    return () => { active = false; };
  }, [doc, machinesGateway, tenantId]);
  const html = useMemo(() => renderOrderDocumentHtml({
    kind: doc.kind,
    documentNumber: doc.number,
    order: doc.snapshot,
    company,
    issuedIso: doc.createdAt,
    machine,
    machines,
  }), [doc, company, machine, machines]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  // Beleg-PDF über den JasperReports-Dienst erzeugen und öffnen (nur wenn ein Dienst konfiguriert ist).
  const onServerPdf = useCallback(async () => {
    setPdfBusy(true);
    setPdfError('');
    try {
      const payload = buildOrderReportPayload({
        kind: doc.kind, documentNumber: doc.number, order: doc.snapshot, company, issuedIso: doc.createdAt, machine, machines,
      });
      openPdfBlob(await fetchReportPdf(reportTemplateForKind[doc.kind], payload));
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'PDF konnte nicht erzeugt werden.');
    } finally {
      setPdfBusy(false);
    }
  }, [doc, company, machine, machines]);
  return (
    <div className="orders-doc-modal" role="dialog" aria-modal="true" aria-label={orderDocumentLabels[doc.kind]} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-doc-modal__panel">
        <header>
          <span>{orderDocumentLabels[doc.kind]} · {doc.number}</span>
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {reportServerConfigured() ? (
              <button
                type="button"
                onClick={onServerPdf}
                disabled={pdfBusy}
                style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#e30613', color: '#fff', font: 'inherit', fontWeight: 600, cursor: pdfBusy ? 'default' : 'pointer', opacity: pdfBusy ? 0.6 : 1 }}
              >
                {pdfBusy ? 'PDF wird erzeugt…' : 'Als PDF'}
              </button>
            ) : null}
            <button type="button" className="orders-doc-modal__close" onClick={onClose}>Schließen</button>
          </span>
        </header>
        {pdfError ? <p role="alert" style={{ color: '#c0392b', margin: '6px 14px 0', fontSize: 13 }}>{pdfError}</p> : null}
        <iframe className="orders-doc-frame" title={orderDocumentLabels[doc.kind]} srcDoc={html} />
      </div>
    </div>
  );
}
